// The session's steps laid along the telemetry time axis (#200), for the
// phase strip under the chart.
//
// Since #200 the recorder stamps the first row of each step with
// `ph: { id, label, type }`, so the steps line up exactly, pauses included.
// A pause is marked too, as its own step. Older sessions only have the phase
// log (each step's length, no start time, pauses not counted): those are laid
// end to end from the start and flagged approximate, but only when the steps
// add up to about the length of the recording. When they fall well short a
// pause or a long count-in happened somewhere unknown, and every step after
// it would sit in the wrong place, so there's no strip at all.
//
// Pure, so it can be tested without the chart.

// How far the steps may fall short of the recording and still be drawn.
const FIT_SLACK_S = 60;
const FIT_SLACK_SHARE = 0.05;

const typeIndex = (plan) => {
  const types = {};
  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (node.id != null && node.type) types[node.id] = node.type;
    (node.children || []).forEach(walk);
  };
  walk(plan);
  return types;
};

/**
 * @returns {{ segments: Array<{start:number,end:number,id:any,label:string,type:string}>, exact: boolean }}
 *   `start`/`end` are seconds on the samples' `t` axis, end exclusive.
 */
export function phaseSegments(samples, { phaseLog = null, plan = null } = {}) {
  if (!samples?.length) return { segments: [], exact: true };
  const first = samples[0].t;
  const last = samples[samples.length - 1].t + 1;

  const marked = [];
  for (const row of samples) {
    if (!row.ph) continue;
    const prev = marked[marked.length - 1];
    if (prev) prev.end = row.t;
    marked.push({ start: row.t, end: last, id: row.ph.id, label: row.ph.label || "", type: row.ph.type || "" });
  }
  if (marked.length) {
    return { segments: marked.filter((s) => s.end > s.start), exact: true };
  }

  if (!Array.isArray(phaseLog) || !phaseLog.length) return { segments: [], exact: true };
  const logged = phaseLog.reduce(
    (sum, entry) => sum + Math.max(0, Math.round(Number(entry?.actualDurationSec) || 0)),
    0,
  );
  const slack = Math.max(FIT_SLACK_S, (last - first) * FIT_SLACK_SHARE);
  if (Math.abs(last - first - logged) > slack) return { segments: [], exact: false };
  const types = typeIndex(plan);
  const segments = [];
  let at = first;
  for (const entry of phaseLog) {
    const length = Math.max(0, Math.round(Number(entry?.actualDurationSec) || 0));
    if (!length) continue;
    if (at >= last) break;
    segments.push({
      start: at,
      end: Math.min(last, at + length),
      id: entry.nodeId,
      label: entry.nodeLabel || "",
      type: types[entry.nodeId] || "",
    });
    at += length;
  }
  return { segments, exact: false };
}

/** Average and peak heart rate and power over one segment of raw samples. */
export function segmentStats(samples, segment) {
  const acc = { hr: [], w: [] };
  for (const row of samples || []) {
    if (row.t < segment.start || row.t >= segment.end) continue;
    if (row.hr != null) acc.hr.push(row.hr);
    if (row.w != null) acc.w.push(row.w);
  }
  const describe = (values) =>
    values.length
      ? {
          avg: Math.round(values.reduce((a, b) => a + b, 0) / values.length),
          max: Math.max(...values),
        }
      : null;
  return { seconds: segment.end - segment.start, hr: describe(acc.hr), power: describe(acc.w) };
}

/** The strip colour family for a step type. */
export function phaseKind(type, label = "") {
  const t = String(type || "").toLowerCase();
  if (t === "pause") return "pause";
  if (t === "transition" || /^(transition|setup)/i.test(String(label || "").trim())) return "transition";
  if (t === "rest" || t === "setrest") return "rest";
  if (t === "warmup") return "warmup";
  if (t === "cooldown") return "cooldown";
  if (t === "work") return "work";
  return "other";
}
